import React, { useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, Filter, PieChart, Search } from 'lucide-react';
import { getAudits } from '../../services/auditAPI';
import { setAudits } from '../../redux/slices/auditSlice';
import { useNavigate } from 'react-router-dom';

// Type definitions


function App() {
  // State for filters and audits
  const [audits, setAuditsState] = React.useState([]);
  const [filteredAudits, setFilteredAudits] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [areaFilter, setAreaFilter] = React.useState('All');
  const [dateRange, setDateRange] = React.useState({ start: '', end: '' });
  const [isLoading, setIsLoading] = React.useState(true);
  const navigate = useNavigate()
  // Fetch audits on component mount
  useEffect(() => {
    const fetchAudits = async () => {
      try {
        setIsLoading(true);
        const response = await getAudits();
        setAuditsState(response);
        setFilteredAudits(response);
        setAudits(response); // Update Redux store
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching audits:', error);
        setIsLoading(false);
      }
    };

    fetchAudits();
  }, []);

  const handleNavigate = (auditId)=>{
    navigate(`/admin/audit-details/${auditId}`)
  }

  // Apply filters when any filter changes
  useEffect(() => {
    let result = audits;

    // Apply text search filter
    if (searchTerm) {
      result = result.filter(audit => 
        audit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        audit.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'All') {
      result = result.filter(audit => audit.status === statusFilter);
    }

    // Apply area filter
    if (areaFilter !== 'All') {
      result = result.filter(audit => audit?.area?.name === areaFilter);
    }

    // Apply date range filter
    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start).getTime();
      const endDate = new Date(dateRange.end).getTime();
      
      result = result.filter(audit => {
        const auditDate = new Date(audit.createdAt).getTime();
        return auditDate >= startDate && auditDate <= endDate;
      });
    }

    setFilteredAudits(result);
  }, [searchTerm, statusFilter, areaFilter, dateRange, audits]);

  // Get unique areas for the filter
  const uniqueAreas = React.useMemo(() => {
    const areas = audits.map(audit => audit?.area?.name);
    return ['All', ...new Set(areas)];
  }, [audits]);

  // Statistics calculation
  const statistics = React.useMemo(() => {
    return {
      total: audits.length,
      completed: audits.filter(audit => audit.status === 'Completed').length,
      inProgress: audits.filter(audit => audit.status === 'In Progress').length,
      pending: audits.filter(audit => audit.status === 'Pending').length,
      // overdue: audits.filter(audit => audit.status === 'Overdue').length,
    };
  }, [audits]);

  // Calculate chart data from actual audits
  const statusData = React.useMemo(() => {
    const statusCounts = audits.reduce((acc, audit) => {
      acc[audit.status] = (acc[audit.status] || 0) + 1;
      return acc;
    }, {} );

    return [
      { status: 'Completed', count: statusCounts['Completed'] || 0, color: '#10B981' },
      { status: 'In Progress', count: statusCounts['In Progress'] || 0, color: '#3B82F6' },
      { status: 'Pending', count: statusCounts['Pending'] || 0, color: '#F59E0B' },
      // { status: 'Overdue', count: statusCounts['Overdue'] || 0, color: '#EF4444' },
    ];
  }, [audits]);

  const areaData = React.useMemo(() => {
    const areaCounts = audits.reduce((acc, audit) => {
      acc[audit?.area?.name] = (acc[audit?.area?.name] || 0) + 1;
      return acc;
    }, {} );

    return Object.entries(areaCounts).map(([name, count]) => ({
      name,
      count,
    }));
  }, [audits]);

  const monthlyData = React.useMemo(() => {
    const monthCounts = audits.reduce((acc, audit) => {
      const month = new Date(audit.createdAt).toLocaleString('default', { month: 'short' });
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {} );

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map(month => ({
      month,
      audits: monthCounts[month] || 0,
    }));
  }, [audits]);

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setAreaFilter('All');
    setDateRange({ start: '', end: '' });
  };

  // Render bar chart
  const renderBarChart = () => {
    const maxCount = Math.max(...areaData.map(item => item.count));
    
    return (
      <div className="flex flex-col h-full p-2">
        <h3 className="text-lg font-semibold  ">Audits by Area</h3>
        <div className="flex-1 flex items-end space-x-4">
          {areaData.map((item, index) => (
            <div key={index} className="flex flex-col items-center">
              <div className="text-xs mb-1">{item.count}</div>
              <div 
                className="w-12 bg-blue-500 rounded-t-md transition-all duration-500 ease-in-out hover:bg-blue-600"
                style={{ height: `${(item.count / maxCount) * 100}%`, minHeight: '40px' }}
              ></div>
              <div className="text-xs mt-1 text-gray-600">{item.name}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render line chart
  const renderLineChart = () => {
    const maxAudits = Math.max(...monthlyData.map(item => item.audits));
    const points = monthlyData.map((item, index) => {
      const x = (index / (monthlyData.length - 1)) * 100;
      const y = 100 - ((item.audits / maxAudits) * 100);
      return `${x},${y}`;
    }).join(' ');
    
    return (
      <div className="flex flex-col h-full p-2">
        <h3 className="text-lg font-semibold">Monthly Audit Trend</h3>
        <div className="flex-1 relative">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polyline
              points={points}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
            {monthlyData.map((item, index) => {
              const x = (index / (monthlyData.length - 1)) * 100;
              const y = 100 - ((item.audits / maxAudits) * 100);
              return (
                <circle 
                  key={index} 
                  cx={x} 
                  cy={y} 
                  r="1.5" 
                  fill="#3B82F6" 
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </svg>
          
          <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-600">
            {monthlyData.map((item, index) => (
              <div key={index}>{item.month}</div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render donut chart
  const renderDonutChart = () => {
    const total = statusData.reduce((acc, item) => acc + item.count, 0);
    let cumulativePercent = 0;
    
    return (
      <div className="flex flex-col h-full p-2">
        <h3 className="text-lg font-semibold ">Audit Status Distribution</h3>
        <div className="flex justify-center">
          <div className="relative w-40 h-40">
            <svg className="w-full h-full" viewBox="0 0 42 42">
              <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#f3f4f6" strokeWidth="3"></circle>
              
              

              {statusData.map((item, index) => {
                const safeTotal = total > 0 ? total : 1; // prevent divide-by-zero
                const percent = (item.count / safeTotal) * 100;

                const strokeDasharray = `${percent} ${100 - percent}`;
                const strokeDashoffset = 100 - cumulativePercent;

                cumulativePercent += percent;

                return (
                  <circle
                    key={index}
                    cx="21"
                    cy="21"
                    r="15.91549430918954"
                    fill="transparent"
                    stroke={item.color}
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={isNaN(strokeDashoffset) ? 0 : strokeDashoffset}
                    style={{ transition: 'all 0.3s ease' }}
                  />
                );
              })}

              
              <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="5" fontWeight="bold">
                {total} Audits
              </text>
            </svg>
          </div>
        </div>
        
        <div className="mb-4 flex flex-wrap justify-center gap-2">
          {statusData.map((item, index) => (
            <div key={index} className="flex items-center text-sm">
              <div className="w-3 h-3 mr-1 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span>{item.status}: {item.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center ">
          <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
          <div className="text-sm text-gray-600">
            <span className="font-medium">Today:</span> {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
            <div className="flex justify-between items-start px-2">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Audits</p>
                <p className="text-2xl font-bold">{statistics.total}</p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <PieChart size={20} className="text-blue-500" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
            <div className="flex justify-between items-start px-2">
              <div>
                <p className="text-sm text-gray-500 font-medium">Completed</p>
                <p className="text-2xl font-bold">{statistics.completed}</p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle size={20} className="text-green-500" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-500">
            <div className="flex justify-between items-start px-2">
              <div>
                <p className="text-sm text-gray-500 font-medium">Pending</p>
                <p className="text-2xl font-bold">{statistics.pending}</p>
              </div>
              <div className="bg-yellow-100 p-2 rounded-lg">
                <Clock size={20} className="text-yellow-500" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
            <div className="flex justify-between items-start px-2">
              <div>
                <p className="text-sm text-gray-500 font-medium">In Progress</p>
                <p className="text-2xl font-bold">{statistics.inProgress}</p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <AlertTriangle size={20} className="text-red-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 h-64">
            {renderDonutChart()}
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 h-64">
            {renderBarChart()}
          </div>
          {/* <div className="bg-white rounded-lg shadow-sm p-4 h-64">
            {renderLineChart()}
          </div> */}
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative w-full md:w-1/3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search audits..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="w-full md:w-1/5">
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All Status</option>
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
                
              </select>
            </div>
            
            <div className="w-full md:w-1/5">
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
              >
                {uniqueAreas.map((area, index) => (
                  <option key={index} value={area}>{area}</option>
                ))}
              </select>
            </div>
            
            <div className="flex space-x-2 w-full md:w-auto">
              <input
                type="date"
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
              <span className="self-center">to</span>
              <input
                type="date"
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
            
            <button
              className="flex items-center space-x-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-all"
              onClick={resetFilters}
            >
              <Filter size={16} />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Audits Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Audit List</h2>
            <p className="text-sm text-gray-500">{filteredAudits.length} audits found</p>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Created</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAudits.map(audit => (
                      <tr key={audit._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{audit.title}</div>
                          <div className="text-xs text-gray-500">{audit.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            audit.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            audit.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            audit.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {audit.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {audit?.area?.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(audit.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {audit.createdBy.firstName} {audit.createdBy.lastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button onClick={() => handleNavigate(audit._id)} className="text-blue-600 hover:text-blue-900 mr-2">View</button>
                          <button onClick={() => handleNavigate(audit._id)} className="text-green-600 hover:text-green-900">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredAudits.length}</span> of{' '}
                        <span className="font-medium">{filteredAudits.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                          Previous
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                          1
                        </button>
                        <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;